import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { expertService } from '@/services/api'
import type { ExpertProfile } from '@/types'

export default function ExpertPublicPage() {
  const { id } = useParams<{ id: string }>()

  const { data: expert, isLoading, error } = useQuery({
    queryKey: ['expert', id],
    queryFn: async () => {
      const response = await expertService.getPublicProfile(id!)
      return response.data as ExpertProfile
    },
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    )
  }

  if (error || !expert) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Expert not found</h1>
        <Link to="/experts" className="mt-4 text-primary-600 hover:text-primary-500">
          ← Back to experts
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/experts" className="text-primary-600 hover:text-primary-500 text-sm mb-4 inline-block">
        ← Back to experts
      </Link>

      <div className="card">
        {/* Header */}
        <div className="flex items-start gap-6 pb-6 border-b">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-2xl font-semibold">
            {expert.first_name?.[0]}{expert.last_name?.[0]}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {expert.display_name || `${expert.first_name} ${expert.last_name}`}
              </h1>
              {expert.verified_at && (
                <span className="badge-success">Verified</span>
              )}
            </div>
            <p className="text-lg text-gray-600 mt-1">{expert.headline}</p>
            <p className="text-gray-500 mt-1">{expert.city}, {expert.country}</p>

            <div className="flex gap-4 mt-4">
              {expert.linkedin_url && (
                <a href={expert.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                  LinkedIn
                </a>
              )}
              {expert.github_url && (
                <a href={expert.github_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                  GitHub
                </a>
              )}
              {expert.portfolio_url && (
                <a href={expert.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                  Portfolio
                </a>
              )}
            </div>
          </div>

          <div className="text-right">
            {expert.hourly_rate_min && (
              <div>
                <span className="text-2xl font-bold text-gray-900">
                  €{expert.hourly_rate_min} - €{expert.hourly_rate_max}
                </span>
                <span className="text-gray-500">/hr</span>
              </div>
            )}
            <div className="mt-2 space-x-2">
              {expert.available_for_hire && (
                <span className="badge-success">Available for hire</span>
              )}
              {expert.available_for_contract && (
                <span className="badge-info">Available for contract</span>
              )}
            </div>
          </div>
        </div>

        {/* Bio */}
        {expert.bio && (
          <div className="py-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">About</h2>
            <p className="text-gray-600 whitespace-pre-wrap">{expert.bio}</p>
          </div>
        )}

        {/* Skills */}
        {expert.skills && expert.skills.length > 0 && (
          <div className="py-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Skills</h2>
            <div className="flex flex-wrap gap-2">
              {expert.skills.map((skill) => (
                <span
                  key={skill.id}
                  className={`badge ${
                    skill.verified_level ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {skill.skill_name}
                  {skill.verified_level && (
                    <span className="ml-1">✓ {skill.verified_level}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Contact CTA */}
        <div className="pt-6 border-t text-center">
          <p className="text-gray-600 mb-4">Interested in working with this expert?</p>
          <Link to="/login?type=company" className="btn-primary">
            Sign in to contact
          </Link>
        </div>
      </div>
    </div>
  )
}
